import * as React from "react"
import {useEffect, useRef, useState} from "react"
import {getSubmit} from "@ui-seeker/utils"

import {SitesMultiSelect} from "@components"
import {BACKEND_APIS} from "@constants"
import {createBatchFormData} from "../../@signals"


/**
 * war2war only: the customer's OWN sites, where the candidate catalog lives.
 *
 * raw2raw never needed this picker — its own side is derived from the target's market —
 * but war2war runs the other way round, so the operator has to be able to say which own
 * catalog a new competitor listing should be matched against. Left empty, ia-normalizer
 * uses every site the customer has a catalog on.
 */
export default function OwnSitesAutocomplete({value, onChange, disabled, ...props}) {
	// Controlled mode (`value` + `onChange`) exists for the SCHEDULE drawers, whose
	// ownSites lives in the schedule's own config rather than in the create-batch form.
	// Without it a war2war schedule's own sites were unsettable — nothing rendered,
	// populated or cleared them — so they leaked in from a half-open Create Batch drawer
	// and were wiped to [] on every edit, which makes ia-normalizer fan the batch out over
	// every site the customer has a catalog on.
	const controlled = value !== undefined
	const [options, setOptions] = useState([])
	const customerID = createBatchFormData.value.customerID
	const isDisabled = !customerID
	const selected = controlled ? (value || []) : (createBatchFormData.value.ownSites || [])
	const firstCustomer = useRef(customerID)

	useEffect(() => {
		if (isDisabled) {
			setOptions([])
			return
		}
		getSubmit(`${BACKEND_APIS.BATCH_ADMIN}/own-sites`, {customerID})
			.then(res => { if (res) setOptions(res) })
			.catch(err => console.error('Error fetching own sites:', err))
	}, [customerID, isDisabled])

	// Reset when the customer changes: another customer's sites are never valid here.
	// In controlled mode the first render is skipped, or loading a saved schedule would
	// clear the very value it just loaded.
	useEffect(() => {
		if (controlled) {
			if (customerID === firstCustomer.current) return
			firstCustomer.current = customerID
			if (selected.length > 0) onChange([])
			return
		}
		if (createBatchFormData.value.ownSites?.length > 0) {
			createBatchFormData.value = {...createBatchFormData.value, ownSites: []}
		}
	}, [customerID])

	const valueObjects = selected
		.map(v => (v && typeof v === 'object') ? v : options.find(o => o.value === v))
		.filter(Boolean)

	const handleChange = (newValue) => {
		const ids = newValue.map(o => o.value)
		if (controlled) onChange(ids)
		else createBatchFormData.value = {...createBatchFormData.value, ownSites: ids}
	}

	return <SitesMultiSelect
		options={options}
		value={valueObjects}
		onChange={handleChange}
		label="Own Sites (optional)"
		placeholder={customerID ? "Search own sites... (empty = all)" : "Select a Customer first"}
		// Combined, not overridden: a caller passing disabled={false} must not be able to
		// re-enable the field while there is no customer to resolve the sites against.
		disabled={isDisabled || !!disabled}
		{...props}
	/>
}
